import axios from '@/api/config/axios';
import { executeWithPlanOverageConsentRetry } from '@/composables/usePlanOverageConsent';

function unwrapListPayload(payload) {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.data)) return payload.data;
    return [];
}

/**
 * Professores com aceita_revisoes_texto_terceiros (exceto o usuário logado), para designação na turma.
 * @param {{ search?: string, per_page?: number, signal?: AbortSignal }} params
 */
export async function fetchRevisoresTextoDisponiveis(params = {}) {
    const perPage = Math.min(Math.max(Number(params.per_page) || 20, 1), 50);
    const response = await axios.get('/api/v1/human-text-review/revisores-disponiveis', {
        params: {
            search: params.search ?? '',
            per_page: perPage,
        },
        signal: params.signal,
    });
    const root = response?.data?.data;
    return unwrapListPayload(root);
}

/**
 * Opt-in do professor para aparecer na listagem de revisores de texto de terceiros.
 * @param {boolean} aceita
 */
function normalizePaginatedEnvelope(root, pageFallback = 1, perPageFallback = 15) {
    let items = [];
    let total = 0;
    let lastPage = 1;
    let currentPage = pageFallback;
    let perPage = perPageFallback;

    if (Array.isArray(root)) {
        items = root;
        total = items.length;
        return { items, total, lastPage, currentPage, perPage };
    }

    if (!root || typeof root !== 'object') {
        return { items, total, lastPage, currentPage, perPage };
    }

    if (Array.isArray(root.data)) {
        items = root.data;
    }

    const pagination = root.pagination && typeof root.pagination === 'object' ? root.pagination : null;
    if (pagination) {
        total = Number(pagination.total) || items.length;
        lastPage = Math.max(Number(pagination.last_page) || 1, 1);
        currentPage = Number(pagination.current_page) || pageFallback;
        perPage = Math.max(Number(pagination.per_page) || perPageFallback, 1);
        return { items, total, lastPage, currentPage, perPage };
    }

    total = Number(root.total) || items.length;
    lastPage = Math.max(Number(root.last_page) || 1, 1);
    currentPage = Number(root.current_page) || pageFallback;
    perPage = Math.max(Number(root.per_page) || perPageFallback, 1);
    return { items, total, lastPage, currentPage, perPage };
}

/**
 * Documentos em revisao_texto_manual para turmas em que o professor é revisor designado.
 * @param {{ per_page?: number, page?: number, orderBy?: string, ascending?: boolean|string }} params
 * @returns {Promise<{ items: any[], total: number, lastPage: number, currentPage: number, perPage: number }>}
 */
export async function fetchHumanTextReviewFila(params = {}) {
    const perPage = Math.min(Math.max(Number(params.per_page) || 15, 1), 50);
    const page = Math.max(Number(params.page) || 1, 1);
    const hasOrderBy = typeof params.orderBy === 'string' && params.orderBy.trim().length > 0;
    const ascendingValue =
        typeof params.ascending === 'string'
            ? params.ascending
            : params.ascending === true
              ? 'true'
              : params.ascending === false
                ? 'false'
                : undefined;
    const response = await axios.get('/api/v1/human-text-review/fila', {
        params: {
            per_page: perPage,
            page,
            ...(hasOrderBy ? { orderBy: params.orderBy.trim() } : {}),
            ...(hasOrderBy && ascendingValue ? { ascending: ascendingValue } : {}),
        },
    });
    const root = response?.data?.data;
    return normalizePaginatedEnvelope(root, page, perPage);
}

/**
 * Histórico: documentos em que o usuário logado concluiu revisão humana do texto.
 * @param {{ per_page?: number, page?: number, orderBy?: string, ascending?: boolean|string, signal?: AbortSignal }} params
 * @returns {Promise<{ items: any[], total: number, lastPage: number, currentPage: number, perPage: number }>}
 */
export async function fetchHumanTextReviewHistorico(params = {}) {
    const perPage = Math.min(Math.max(Number(params.per_page) || 15, 1), 50);
    const page = Math.max(Number(params.page) || 1, 1);
    const hasOrderBy = typeof params.orderBy === 'string' && params.orderBy.trim().length > 0;
    const ascendingValue =
        typeof params.ascending === 'string'
            ? params.ascending
            : params.ascending === true
              ? 'true'
              : params.ascending === false
                ? 'false'
                : undefined;
    const response = await axios.get('/api/v1/human-text-review/historico', {
        params: {
            per_page: perPage,
            page,
            ...(hasOrderBy ? { orderBy: params.orderBy.trim() } : {}),
            ...(hasOrderBy && ascendingValue ? { ascending: ascendingValue } : {}),
        },
        signal: params.signal,
    });
    const root = response?.data?.data;
    return normalizePaginatedEnvelope(root, page, perPage);
}

/**
 * Normaliza documento da fila para o formato esperado pelo FilaPendentesCard.
 */
export function normalizeDocumentoFilaRevisaoHumana(doc) {
    if (!doc) return null;
    const id = doc.id ?? doc.uuid;
    if (!id) return null;
    const aluno = doc.user ?? doc.aluno ?? doc.student ?? doc.usuario;
    const turma = doc.turma ?? doc.class;
    const tema = doc.tema ?? doc.theme;
    const nomeAluno = aluno?.name ?? aluno?.nome ?? 'Aluno';
    return {
        id,
        nome_aluno: nomeAluno,
        tema_redacao: tema?.nome ?? tema?.titulo ?? tema?.name ?? doc.tema_nome ?? doc.titulo_tema ?? '',
        nome_turma: turma?.nome ?? turma?.title ?? turma?.name ?? doc.nome_turma ?? '',
        data_submissao: doc.created_at ?? doc.enviado_em ?? doc.updated_at,
        imagem_perfil_aluno:
            aluno?.profile_pic ?? aluno?.profilePic ?? aluno?.avatar ?? aluno?.imagem ?? null,
    };
}

/**
 * @param {boolean} aceita
 * @param {boolean} [notificarNovaRevisaoTextoHumana] — quando `aceita` é true, envie para sincronizar avisos (app + WhatsApp).
 */
export async function putMinhaDisponibilidadeRevisaoTexto(aceita, notificarNovaRevisaoTextoHumana = false) {
    const body = {
        aceita_revisoes_texto_terceiros: Boolean(aceita),
    };
    if (aceita) {
        body.notificar_nova_revisao_texto_humana = Boolean(notificarNovaRevisaoTextoHumana);
    }
    const response = await axios.put('/api/v1/human-text-review/minha-disponibilidade', body);
    return response?.data?.data ?? response?.data ?? null;
}

export function mapUsuarioToRevisorOption(usuario) {
    if (!usuario) return null;
    const value = String(usuario.id ?? usuario.uuid ?? '').trim();
    if (!value) return null;
    const nome = usuario.name ?? usuario.nome ?? '';
    const email = usuario.email ?? '';
    const label = [nome, email].filter(Boolean).join(' — ') || value;
    return { label, value, email, name: nome };
}

/**
 * Alinha payload do endpoint de revisão humana ao formato usado no preview (aluno, id).
 * @param {Record<string, unknown>|null|undefined} doc
 */
export function normalizeDocumentoHumanoParaStore(doc) {
    if (!doc || typeof doc !== 'object') return null;
    const d = { ...doc };
    if (d.user && !d.aluno) {
        d.aluno = d.user;
    }
    return d;
}

/**
 * Documento completo para o revisor (substitui GET /documento/:id quando aplicável).
 * @param {string} documentoId
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function fetchHumanTextReviewDocumento(documentoId, options = {}) {
    const response = await axios.get(`/api/v1/human-text-review/documento/${documentoId}`, {
        signal: options.signal,
    });
    return normalizeDocumentoHumanoParaStore(response?.data?.data ?? null);
}

/**
 * Validação antes de concluir (créditos, permissão, etc.).
 * @param {string} documentoId
 */
export async function fetchHumanTextReviewPrecheck(documentoId) {
    const response = await axios.get(`/api/v1/human-text-review/documento/${documentoId}/precheck`);
    return response?.data?.data ?? response?.data ?? null;
}

/**
 * Conclui a revisão humana com o texto final.
 * @param {string} documentoId
 * @param {string} texto
 */
export async function postHumanTextReviewConcluir(documentoId, texto) {
    const response = await executeWithPlanOverageConsentRetry(
        () =>
            axios.post(`/api/v1/human-text-review/documento/${documentoId}/concluir`, {
                texto,
            }),
        'revisoes_texto_humano'
    );
    return response?.data?.data ?? response?.data ?? null;
}
